import { Button } from "@progress/kendo-react-buttons";
import { NumericTextBox, RadioGroup, TextArea } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import quotesApi from "../../../api/quotes";
import { ReviewQuoteRequest } from "../../../types/quotes/review";
import { LoadingIndicator } from "../../LoadingIndicator";

const APPROVE = "Approve";
const REJECT = "Reject";

type Props = {
  quoteId: number;
  getQuoteDetails(): any;
  onReject(): any;
  totalVolume: number;
  unitPriceDecimals: number;
  allowOverride: boolean;
  quote: any;
};

const QuoteReviewForm = ({
  quoteId,
  quote,
  getQuoteDetails,
  onReject,
  totalVolume,
  unitPriceDecimals,
  allowOverride,
}: Props) => {
  const { mutate, isPending } = useMutation({
    mutationFn: (data: ReviewQuoteRequest) => quotesApi.reviewQuote(data),
    onSuccess: (_, { isApproval }) => {
      if (isApproval) {
        getQuoteDetails();
      } else {
        onReject();
      }
    },
  });

  unitPriceDecimals = 4;

  const { submittal, pricingId, hasBroker, hasFronter } = quote;

  const [priceOverride, setPriceOverride] = useState<number>(submittal.unitPrice);
  const [brokerPriceOverride, setBrokerPriceOverride] = useState<number>(submittal.brokerUnitPrice);
  const [fronterPriceOverride, setFronterPriceOverride] = useState<number>(
    submittal.fronterUnitPrice,
  );

  const [isApproval, setIsApproval] = useState<boolean | null>(null);
  const isRejection = isApproval === false;

  const [comments, setComments] = useState<string | undefined>();

  const hasOverride =
    priceOverride.toFixed(unitPriceDecimals) !== submittal.unitPrice.toFixed(unitPriceDecimals);

  const hasBrokerOverride =
    hasBroker &&
    brokerPriceOverride.toFixed(unitPriceDecimals) !==
      submittal.brokerUnitPrice.toFixed(unitPriceDecimals);

  const hasFronterOverride =
    hasFronter &&
    fronterPriceOverride.toFixed(unitPriceDecimals) !==
      (submittal.fronterUnitPrice ?? 0).toFixed(unitPriceDecimals);

  const showButtons = isApproval !== null;
  const showCommentsInput = isRejection || hasOverride || hasBrokerOverride || hasFronterOverride;

  const handleClickCancel = () => {
    setIsApproval(null);
    setComments(undefined);
  };

  const handleClickComplete = () => {
    if (isApproval === null) return;

    const data: ReviewQuoteRequest = {
      quoteId,
      pricingId,
      isApproval: isApproval as boolean,
      totalVolume,
      priceOverride: hasOverride ? priceOverride : null,
      brokerPriceOverride: hasBrokerOverride ? brokerPriceOverride : null,
      fronterPriceOverride: hasFronterOverride ? fronterPriceOverride : null,
      comments,
    };
    mutate(data);
  };

  const handleChangeOverride = (event: any) => {
    const value = event.value;
    setPriceOverride(value);
  };

  const handleChangeBrokerOverride = (event: any) => {
    const value = event.value;
    setBrokerPriceOverride(value);
  };

  const handleChangeFronterOverride = (event: any) => {
    const value = event.value;
    setFronterPriceOverride(value);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <RadioGroup
          data={[
            { value: APPROVE, label: APPROVE },
            { value: REJECT, label: REJECT },
          ]}
          value={isApproval ? APPROVE : isRejection ? REJECT : ""}
          onChange={(e: any) => setIsApproval(e.value === APPROVE)}
          layout="horizontal"
        />
      </div>
      <div style={{ display: "flex" }}>
        {isApproval && allowOverride && (
          <>
            {hasFronter && (
              <div className="margin-5">
                <Label>
                  Fronter: &nbsp; &nbsp;
                  <NumericTextBox
                    value={fronterPriceOverride}
                    onChange={handleChangeFronterOverride}
                    format={`c${unitPriceDecimals}`}
                    spinners={false}
                    width={100}
                  />
                </Label>
              </div>
            )}
            {hasBroker && (
              <div className="margin-5">
                <Label>
                  Broker: &nbsp; &nbsp;
                  <NumericTextBox
                    value={brokerPriceOverride}
                    onChange={handleChangeBrokerOverride}
                    format={`c${unitPriceDecimals}`}
                    spinners={false}
                    width={100}
                  />
                </Label>
              </div>
            )}
            <div className="margin-5">
              <Label>
                Client: &nbsp; &nbsp;
                <NumericTextBox
                  value={priceOverride}
                  onChange={handleChangeOverride}
                  format={`c${unitPriceDecimals}`}
                  width={100}
                  spinners={false}
                />
              </Label>
            </div>
          </>
        )}
      </div>

      {showCommentsInput && (
        <div className="margin-5">
          <TextArea
            value={comments}
            onChange={(e) => setComments(e.target.value as string)}
            placeholder="Comments. . ."
            size="large"
            draggable={false}
          />
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "center" }}>
        {showButtons ? (
          <>
            <Button themeColor="primary" onClick={handleClickComplete} disabled={isPending}>
              Confirm
            </Button>

            <Button
              themeColor="secondary"
              fillMode="solid"
              onClick={handleClickCancel}
              disabled={isPending}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button style={{ visibility: "hidden", width: 1 }}>&nbsp;</Button>
        )}
      </div>

      <LoadingIndicator loading={isPending} />
    </div>
  );
};

export default QuoteReviewForm;
